import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronRight, ChevronDown, Lock, Building2, TrendingUp, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { RolePickerModal, LocationPickerModal } from './Pickers';
import { getProfile, getCompanyInsights, searchOrganizations } from '../api/insights';
import {
  Card, CardContent, CardTitle, CardTag, SeeMore,
  IllustrationHiringNow, IllustrationTenure, IllustrationRoleFunnel,
  IllustrationReferral, IllustrationCareerPath, IllustrationLocation
} from './VisualInsights';

export default function OnboardingGoalFocus() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const routeState = location.state || {};

  const nextPath = routeState.nextPath || '/dashboard';
  const nextState = routeState.nextState || { startTour: true };
  const orgId = routeState.orgId ?? nextState?.orgId;
  const company = routeState.companyName || nextState?.companyName || 'Google';
  const initialRole = routeState.role || 'Software Engineer';
  const [resolvedOrgId, setResolvedOrgId] = useState(orgId || null);

  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState(initialRole);
  const [loc, setLoc] = useState('Bangalore');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [onboardingInsightsData, setOnboardingInsightsData] = useState(null);
  const [profileRoleHints, setProfileRoleHints] = useState([]);
  const [profileLocationHints, setProfileLocationHints] = useState([]);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [hasWork, setHasWork] = useState(false);
  const [hasEdu, setHasEdu] = useState(false);
  const [workTabs, setWorkTabs] = useState([]);
  const [collegeTabs, setCollegeTabs] = useState([]);
  const [workTabRefIds, setWorkTabRefIds] = useState({});
  const [collegeTabRefIds, setCollegeTabRefIds] = useState({});
  const [profileChecking, setProfileChecking] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  const normalizeList = (values = [], limit = 6) =>
    Array.from(
      new Set(
        values
          .map((v) => String(v || '').trim())
          .filter(Boolean)
      )
    ).slice(0, limit);

  useEffect(() => {
    getProfile()
      .then(p => {
        const root = p?.profile || p || {};
        const work = Array.isArray(root.work_experiences) && root.work_experiences.some(w => w.company);
        const edu = Array.isArray(root.educations) && root.educations.some(e => e.college);
        const profileWorkCompanies = normalizeList((root.work_experiences || []).map((w) => w?.company));
        const profileColleges = normalizeList((root.educations || []).map((e) => e?.college));
        const openRoles = normalizeList(
          (root.open_to_roles || []).map((r) => (typeof r === 'string' ? r : r?.role)),
          10
        );
        const expLocations = normalizeList((root.work_experiences || []).map((w) => w?.location), 10);
        setHasWork(work);
        setHasEdu(edu);
        setProfileComplete(work || edu);
        setWorkTabs(profileWorkCompanies);
        setCollegeTabs(profileColleges);
        setProfileRoleHints(openRoles);
        setProfileLocationHints(expLocations);
      })
      .catch(() => setProfileComplete(false))
      .finally(() => setProfileChecking(false));
  }, []);

  useEffect(() => {
    if (orgId) {
      setResolvedOrgId(orgId);
      return;
    }
    const candidateName = String(company || nextState?.companyName || '').trim();
    if (!candidateName) return;
    searchOrganizations(candidateName)
      .then((results) => {
        const matched = Array.isArray(results) ? results[0] : null;
        if (matched?.id) setResolvedOrgId(matched.id);
      })
      .catch(() => { });
  }, [orgId, company, nextState?.companyName]);

  useEffect(() => {
    if (!resolvedOrgId) return;
    getCompanyInsights(resolvedOrgId, {
      includeCore: true,
      includeNetwork: false,
      includeRole: true,
      includeLocation: true,
    }).then(data => {
      if (data) {
        setOnboardingInsightsData(data);
        const mergedRoles = normalizeList([
          ...(data.available_roles || []),
          ...profileRoleHints,
          role
        ], 15);
        const mergedLocations = normalizeList([
          ...(data.available_locations || []),
          ...profileLocationHints,
          loc
        ], 15);
        setAvailableRoles(mergedRoles);
        setAvailableLocations(mergedLocations);
      }
    }).catch(console.error);
  }, [resolvedOrgId, profileRoleHints, profileLocationHints, role, loc]);

  // Keep onboarding light: do not precompute both network families here.
  // The selected work/education path will fetch only the clicked company/college on demand.

  useEffect(() => {
    if (location.state?.autoOpenReferral) setSelected('referral');
  }, [location.state?.autoOpenReferral]);

  const resolveInsightsTarget = async () => {
    if (resolvedOrgId) return { orgId: resolvedOrgId, companyName: String(company || '').trim() || 'Google' };
    const candidateName = String(company || nextState?.companyName || '').trim();
    if (!candidateName) return null;
    try {
      const results = await searchOrganizations(candidateName);
      const matched = Array.isArray(results) ? results[0] : null;
      if (!matched?.id) return null;
      return {
        orgId: matched.id,
        companyName: String(matched.name || candidateName).trim() || candidateName,
      };
    } catch {
      return null;
    }
  };

  const goToBlock = async (mode, extra = {}) => {
    const target = await resolveInsightsTarget();
    if (!target?.orgId) return;
    const sharedState = {
      ...nextState,
      orgId: target.orgId,
      companyName: target.companyName,
      selectedRole: role,
      selectedLocation: loc,
      fromInsightsTransition: true,
      ...extra,
    };

    const directListModes = new Set(['work_network', 'education_network', 'cross_network']);
    if (directListModes.has(mode) && onboardingInsightsData) {
      navigate('/insights', {
        state: {
          ...sharedState,
          sourceData: null,
          autoNavigateToBlock: mode,
          mode,
        }
      });
      return;
    }

    navigate('/insights', {
      state: {
        ...sharedState,
        autoNavigateToBlock: mode,
      }
    });
  };

  const goToAll = async () => {
    const target = await resolveInsightsTarget();
    if (target?.orgId) {
      navigate('/insights', {
        state: {
          ...nextState,
          orgId: target.orgId,
          companyName: target.companyName,
          selectedRole: role,
          selectedLocation: loc,
        }
      });
      return;
    }
    navigate(nextPath, {
      state: {
        ...nextState,
        orgId: orgId ?? nextState?.orgId,
        companyName: company || nextState?.companyName,
      }
    });
  };

  const goToProfile = () => navigate('/profile', { state: { returnTo: '/onboarding-goal-focus', returnState: location.state } });

  const skipAll = () => navigate(nextPath, {
    state: {
      ...nextState,
      orgId: orgId ?? nextState?.orgId,
      companyName: company || nextState?.companyName,
      startTour: true
    },
    replace: true
  });

  // Theme
  const accent = isDark ? '#F59E0B' : '#2563eb';
  const txt = isDark ? 'rgba(255,255,255,0.92)' : '#1e293b';
  const sub = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
  const cardBrd = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const skipBrd = isDark ? 'rgba(255,255,255,0.1)' : '#cbd5e1';
  const appCls = isDark ? 'dark-app' : 'light-app';
  const solidTxt = isDark ? '#1a1612' : '#ffffff';

  const GoalCard = ({ num, title, desc, icon, color, locked, onClick, cta, bg }) => (
    <motion.div
      whileHover={!locked ? { y: -3, boxShadow: isDark ? `0 8px 32px ${color}15` : `0 8px 24px ${color}12`, borderColor: color } : {}}
      whileTap={!locked ? { scale: 0.99 } : {}}
      onClick={locked ? undefined : onClick}
      style={{
        width: '100%', textAlign: 'left', position: 'relative', overflow: 'hidden',
        background: bg || (isDark ? 'rgba(255,255,255,0.02)' : '#fff'),
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        borderRadius: 20, padding: '22px 24px', cursor: locked ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 20, transition: 'all .2s',
        opacity: locked ? 0.7 : 1,
        boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.02)',
      }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 14, flexShrink: 0,
        background: `linear-gradient(135deg, ${color}18, ${color}08)`,
        border: `1px solid ${color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>{num}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: txt, lineHeight: 1.3, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          {title} {locked && <Lock size={14} style={{ color: sub, flexShrink: 0 }} />}
        </div>
        <div style={{ fontSize: 12, color: sub, lineHeight: 1.5 }}>{desc}</div>
        {locked && cta && (
          <motion.button
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={(e) => { e.stopPropagation(); cta.onClick(); }}
            style={{
              background: accent, color: solidTxt,
              border: 0, padding: '10px 20px', borderRadius: 12, fontSize: 13, fontWeight: 700,
              cursor: 'pointer', marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6,
              boxShadow: `0 4px 14px ${accent}40`,
            }}
          >
            {cta.label} <ArrowRight size={14} />
          </motion.button>
        )}
      </div>
      {!locked && (
        <div style={{ alignSelf: 'center', flexShrink: 0 }}>
          <ChevronRight size={18} style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#cbd5e1' }} />
        </div>
      )}
    </motion.div>
  );

  const stepColors = ['#10b981', accent, accent];
  const steps = [
    { label: 'Profile', done: true },
    { label: 'Focus', done: !!selected },
    { label: 'Insights', done: false },
  ];

  return (
    <div className={`${appCls}`} style={{ minHeight: '100vh', fontFamily: "'Manrope', sans-serif", background: isDark ? '#0D1019' : '#F8FAFC' }}>
      <header style={{
        position: 'sticky', top: 0, zIndex: 50, padding: '0 24px', height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: isDark ? 'rgba(13,16,25,0.8)' : 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(16px)', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e8edf5'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <div style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: 8, padding: 3 }}>
            <img src="/assets/logo.png" alt="fairhire" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <span style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: txt }}>fairhire</span>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
          onClick={skipAll}
          style={{
            color: sub, fontSize: 13, fontWeight: 600, border: `1px solid ${skipBrd}`,
            background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(59,130,246,0.04)',
            borderRadius: 999, padding: '7px 16px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          Skip for now <ArrowRight size={13} />
        </motion.button>
      </header>

      <div style={{ maxWidth: 480, margin: '32px auto 0', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <motion.div
                  animate={{ scale: i === 1 ? 1 : 0.85 }}
                  style={{
                    width: i === 1 ? 32 : 28, height: i === 1 ? 32 : 28, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: s.done || i === 1
                      ? `linear-gradient(135deg, ${stepColors[i]}, ${stepColors[i]}bb)`
                      : isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                    border: s.done || i === 1 ? 'none' : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#dbe4ef'}`,
                    color: s.done || i === 1 ? '#fff' : sub,
                    fontSize: 12, fontWeight: 800,
                    boxShadow: i === 1 ? `0 4px 14px ${stepColors[i]}35` : 'none',
                  }}
                >
                  {s.done ? '✓' : i + 1}
                </motion.div>
                <span style={{ fontSize: 10, fontWeight: 700, color: i === 1 ? txt : sub, letterSpacing: '.04em' }}>{s.label}</span>
              </div>
              {i < 2 && (
                <div style={{
                  width: 60, height: 2, borderRadius: 2, marginBottom: 18,
                  background: s.done
                    ? `linear-gradient(90deg, ${stepColors[i]}, ${stepColors[i + 1]})`
                    : isDark ? 'rgba(255,255,255,0.06)' : '#e8ecf4',
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ textAlign: 'center', margin: `${isMobile ? '40px' : '60px'} auto 0`, maxWidth: 560, padding: isMobile ? '0 16px' : '0 24px' }}>
        <motion.h1
          key={selected || 'main'}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          style={{
            fontSize: isMobile ? 22 : 28, fontWeight: 800, lineHeight: 1.25, marginBottom: 10, color: txt,
            fontFamily: "'Playfair Display', serif",
          }}
        >
          {!selected
            ? 'What do you want to find first?'
            : selected === 'proven' ? `Companies proven to hire from ${company}`
              : selected === 'referral' ? 'Your referral network'
                : 'Plan where you go next'}
        </motion.h1>
        <motion.p
          key={`sub-${selected || 'main'}`}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          style={{ fontSize: 14, color: sub, lineHeight: 1.6 }}
        >
          {!selected
            ? 'Pick a goal below — we\'ll prioritize the most relevant signals for you'
            : selected === 'proven' ? 'Sorted by recency and hiring volume. Real hires, not just job posts.'
              : selected === 'referral' ? 'Companies where your network can help you get in'
                : 'Real career paths taken by alumni. Use it to plan your next move.'}
        </motion.p>
      </div>
      <div style={{ height: 32 }} />

      <div style={{ width: '100%', maxWidth: selected ? (isMobile ? '100%' : 860) : (isMobile ? '100%' : 640), margin: '0 auto', padding: isMobile ? '0 16px' : '0' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={selected || 'main'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {!selected ? (
              <>
                <div style={{
                  background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8edf5'}`,
                  borderRadius: 32,
                  padding: isMobile ? '16px' : '24px',
                  boxShadow: isDark ? 'none' : '0 8px 30px rgba(0,0,0,0.04)',
                }}>
                  <div style={{ display: 'grid', gap: isMobile ? 12 : 16 }}>
                    <GoalCard
                      num="HIRING SIGNALS"
                      title="Find companies hiring from your background"
                      desc={`See companies actively hiring from ${company}.`}
                      icon={<Building2 size={22} color="#3b82f6" />}
                      color="#3b82f6"
                      onClick={() => setSelected('proven')}
                    />
                    <GoalCard
                      num="CAREER PATHS"
                      title="Plan your next move"
                      desc="See where alumni land after their first move and which companies keep them the longest."
                      icon={<TrendingUp size={22} color="#8b5cf6" />}
                      color="#8b5cf6"
                      onClick={() => setSelected('plan')}
                    />
                    <GoalCard
                      num="REFERRAL NETWORK"
                      title="Find where you can get a referral"
                      desc={profileComplete ? 'Companies where alumni from your work and college network can refer you.' : 'Complete your profile to unlock referral insights based on your work and education history.'}
                      icon={<Users size={22} color={profileComplete ? '#f59e0b' : sub} />}
                      color="#f59e0b"
                      locked={!profileComplete && !profileChecking}
                      onClick={() => setSelected('referral')}
                      cta={!profileComplete && !profileChecking ? { label: 'Complete profile', onClick: goToProfile } : null}
                    />
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 48 }}>
                  <motion.button
                    whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={goToAll}
                    style={{ border: 0, background: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: sub, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    Skip — show all insights <ArrowRight size={13} />
                  </motion.button>
                </div>
              </>
            ) : selected === 'proven' ? (
              <>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: sub, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20 }}>
                  <ArrowLeft size={14} /> Back to goals
                </button>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 16 : 24, marginBottom: 32 }}>
                  <Card onClick={() => goToBlock('active_hirers')} style={{ height: '100%' }}>
                    <IllustrationHiringNow />
                    <CardContent>
                      <CardTag label="Hiring Now" color="#EF4444" />
                      <CardTitle>Companies actively hiring from {company}</CardTitle>
                      <p style={{ fontSize: 11, color: sub, lineHeight: 1.4, marginBottom: 12 }}>
                        Hired someone from {company} recently
                      </p>
                      <SeeMore label="Find a contact inside" onClick={() => goToBlock('active_hirers')} color="#EF4444" />
                    </CardContent>
                  </Card>

                  <Card onClick={() => goToBlock('next_destinations')} style={{ height: '100%' }}>
                    <IllustrationReferral />
                    <CardContent>
                      <CardTag label="High Volume" color="#3B82F6" />
                      <CardTitle>Where many {company} alumni have landed</CardTitle>
                      <p style={{ fontSize: 11, color: sub, lineHeight: 1.4, marginBottom: 12 }}>
                        Proven Companies that Hired Many People from {company} earlier.
                      </p>
                      <SeeMore label="Get a referral contact" onClick={() => goToBlock('next_destinations')} color="#3B82F6" />
                    </CardContent>
                  </Card>

                  <Card onClick={() => goToBlock('role_hiring', { selectedRole: role })} style={{ height: '100%' }}>
                    <IllustrationRoleFunnel roleLabel={role} />
                    <CardContent>
                      <CardTag label="Filter by role" color="#10B981" />
                      <CardTitle style={{ fontSize: 16, fontWeight: 700, color: txt, lineHeight: 1.5, marginBottom: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        Top companies hiring{' '}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowRolePicker(true); }}
                          style={{
                            color: isDark ? '#3B82F6' : '#2563eb',
                            background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)',
                            border: isDark ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(59,130,246,0.2)',
                            borderRadius: 6, padding: '2px 8px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 4, transform: 'translateY(-1px)'
                          }}
                        >
                          {role} <ChevronDown size={12} />
                        </button>
                        {' '} from {company}
                      </CardTitle>
                      <p style={{ fontSize: 11, color: sub, lineHeight: 1.5, marginBottom: 12 }}>
                        Not every company hires every role. These are the ones actually hiring {role}s from {company}.
                      </p>
                      <SeeMore label="See companies hiring for your role" onClick={() => goToBlock('role_hiring', { selectedRole: role })} color="#10B981" />
                    </CardContent>
                  </Card>

                  <Card onClick={() => goToBlock('location_hiring', { selectedLocation: loc })} style={{ height: '100%' }}>
                    <IllustrationLocation />
                    <CardContent>
                      <CardTag label="Filter by Location" color="#F59E0B" />
                      <CardTitle style={{ fontSize: 16, fontWeight: 700, color: txt, lineHeight: 1.5, marginBottom: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        Moving to{' '}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowLocationPicker(true); }}
                          style={{
                            color: isDark ? '#10B981' : '#059669',
                            background: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.06)',
                            border: isDark ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(16,185,129,0.2)',
                            borderRadius: 6, padding: '2px 8px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 4, transform: 'translateY(-1px)'
                          }}
                        >
                          {loc} <ChevronDown size={12} />
                        </button>
                        {' '}? {company} people already landed here
                      </CardTitle>
                      <p style={{ fontSize: 11, color: sub, lineHeight: 1.5, marginBottom: 12 }}>
                        Companies in {loc} that have hired ex-{company} people before.
                      </p>
                      <SeeMore label="See companies hiring for your location" onClick={() => goToBlock('location_hiring', { selectedLocation: loc })} color="#F59E0B" />
                    </CardContent>
                  </Card>
                </div>
                <motion.button
                  whileHover={{ scale: 1.01, boxShadow: `0 8px 28px ${accent}30` }}
                  whileTap={{ scale: 0.98 }}
                  onClick={goToAll}
                  style={{
                    width: '100%', border: 0, borderRadius: 16, padding: '18px 20px',
                    textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit',
                    background: `linear-gradient(135deg, ${accent}, ${isDark ? '#D97706' : '#1d4ed8'})`,
                    color: '#fff', boxShadow: `0 4px 20px ${accent}25`,
                    fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  Explore all {company} insights <ArrowRight size={18} />
                </motion.button>
                <div style={{ height: 80 }} />
              </>
            ) : selected === 'referral' ? (
              <>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: sub, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20 }}>
                  <ArrowLeft size={14} /> Back to goals
                </button>
                {profileChecking ? (
                  <div style={{
                    textAlign: 'center', padding: '40px 24px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                    borderRadius: 20,
                  }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: txt, marginBottom: 6 }}>Loading your referral profile…</p>
                    <p style={{ fontSize: 12, color: sub, lineHeight: 1.6 }}>We are checking your work and education history.</p>
                  </div>
                ) : !profileComplete ? (
                  <div style={{
                    textAlign: 'center', padding: '40px 24px',
                    background: isDark ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.03)',
                    border: `1px solid ${isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.15)'}`,
                    borderRadius: 20,
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
                      background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Lock size={24} color="#f59e0b" />
                    </div>
                    <p style={{ fontSize: 17, fontWeight: 800, color: txt, marginBottom: 8 }}>Profile needed to unlock referrals</p>
                    <p style={{ fontSize: 13, color: sub, marginBottom: 24, lineHeight: 1.6, maxWidth: 360, margin: '0 auto 24px' }}>
                      Add your work history and education so we can match you with companies where alumni from your network are already working.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                      onClick={goToProfile}
                      style={{
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none',
                        borderRadius: 14, padding: '14px 28px', color: '#fff', fontSize: 14, fontWeight: 700,
                        cursor: 'pointer', boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      Complete your profile <ArrowRight size={15} />
                    </motion.button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 32 }}>
                      {workTabs.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: sub, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>Work Network</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {workTabs.map((companyName) => (
                              <motion.div
                                key={`work-${companyName}`}
                                whileHover={{ y: -2, borderColor: isDark ? 'rgba(139,92,246,0.5)' : 'rgba(139,92,246,0.3)', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(139,92,246,0.02)' }}
                                whileTap={{ scale: 0.99 }}
                                onClick={() => goToBlock('work_network', {
                                  selectedWorkTab: companyName,
                                  selectedWorkTabId: Number(workTabRefIds?.[companyName]) || undefined,
                                })}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                                  background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                                  borderRadius: 16, cursor: 'pointer', transition: 'all .2s'
                                }}
                              >
                                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Users size={20} color="#8B5CF6" />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: txt }}>People from {companyName} now inside these companies</div>
                                </div>
                                <ChevronRight size={18} style={{ color: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1' }} />
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}

                      {collegeTabs.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: sub, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>College Network</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {collegeTabs.map((collegeName) => (
                              <motion.div
                                key={`edu-${collegeName}`}
                                whileHover={{ y: -2, borderColor: isDark ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.3)', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.02)' }}
                                whileTap={{ scale: 0.99 }}
                                onClick={() => goToBlock('education_network', {
                                  selectedCollegeTab: collegeName,
                                  selectedCollegeTabId: Number(collegeTabRefIds?.[collegeName]) || undefined,
                                })}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                                  background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                                  borderRadius: 16, cursor: 'pointer', transition: 'all .2s'
                                }}
                              >
                                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Building2 size={20} color="#3B82F6" />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: txt }}>People from {collegeName} now inside these companies</div>
                                </div>
                                <ChevronRight size={18} style={{ color: isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1' }} />
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.01, boxShadow: `0 8px 28px ${accent}30` }} whileTap={{ scale: 0.98 }}
                      onClick={goToAll}
                      style={{
                        width: '100%', border: 0, borderRadius: 16, padding: '18px 20px',
                        textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit',
                        background: `linear-gradient(135deg, ${accent}, ${isDark ? '#D97706' : '#1d4ed8'})`,
                        color: '#fff', boxShadow: `0 4px 20px ${accent}25`,
                        fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                      }}
                    >
                      Explore all referral paths <ArrowRight size={18} />
                    </motion.button>
                    <div style={{ height: 80 }} />
                  </>
                )}
              </>
            ) : (
              /* ── Plan ahead ── */
              <>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: sub, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20 }}>
                  <ArrowLeft size={14} /> Back to goals
                </button>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 16 : 24, marginBottom: 32 }}>
                  <Card onClick={() => goToBlock('career_paths')} style={{ height: '100%' }}>
                    <IllustrationCareerPath />
                    <CardContent>
                      <CardTag label="Career Paths" color="#8B5CF6" />
                      <CardTitle>Where {company} alumni go after their first move</CardTitle>
                      <p style={{ fontSize: 11, color: sub, lineHeight: 1.4, marginBottom: 12 }}>
                        {company} alumni reach these after first move, you can target it now or use it to plan your next move.
                      </p>
                      <SeeMore label="Explore stepping stone paths" onClick={() => goToBlock('career_paths')} color="#8B5CF6" />
                    </CardContent>
                  </Card>

                  <Card onClick={() => goToBlock('best_tenure')} style={{ height: '100%' }}>
                    <IllustrationTenure />
                    <CardContent>
                      <CardTag label="Stable Workplaces" color="#10B981" />
                      <CardTitle>Stable Workplaces</CardTitle>
                      <p style={{ fontSize: 11, color: sub, lineHeight: 1.4, marginBottom: 12 }}>
                        Long tenure means good managers and real growth, not just a fancy title that leads nowhere.
                      </p>
                      <SeeMore label="See all stable companies" onClick={() => goToBlock('best_tenure')} color="#10B981" />
                    </CardContent>
                  </Card>
                </div>
                <motion.button
                  whileHover={{ scale: 1.01, boxShadow: `0 8px 28px ${accent}30` }} whileTap={{ scale: 0.98 }}
                  onClick={goToAll}
                  style={{
                    width: '100%', border: 0, borderRadius: 16, padding: '18px 20px',
                    textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit',
                    background: `linear-gradient(135deg, ${accent}, ${isDark ? '#D97706' : '#1d4ed8'})`,
                    color: '#fff', boxShadow: `0 4px 20px ${accent}25`,
                    fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  Explore all career insights <ArrowRight size={18} />
                </motion.button>
                <div style={{ height: 80 }} />
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Picker Modals */}
        <AnimatePresence>
          {showRolePicker && (
            <RolePickerModal
              available={availableRoles}
              currentRole={role}
              onSelect={(r) => { setRole(r || initialRole); setShowRolePicker(false); }}
              onClose={() => setShowRolePicker(false)}
            />
          )}
          {showLocationPicker && (
            <LocationPickerModal
              available={availableLocations}
              currentLocation={loc}
              onSelect={(l) => { setLoc(l || 'Bangalore'); setShowLocationPicker(false); }}
              onClose={() => setShowLocationPicker(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
